/**
 * Typed search params for the `/fleet` route. The entry-type / maintenance-category filters and
 * the archived toggle live in the URL so they are deep-linkable AND compile-checked (the inventory
 * `searchParams` pattern). TanStack Router validates the raw URL params through
 * `validateFleetSearch` on every navigation.
 *
 * `?entryType=fuel|maintenance` narrows the entries (legacy parity — the fleet `entry_type` filter).
 * `?maintenanceCategory=<key>` narrows maintenance entries to one category. `?archived=1` flips the
 * card grid to archived vehicles. The coarse endpoint owns the filtering; the SPA only carries the
 * flags and NEVER sends a tenant id (SEC-11).
 */

import type { FleetEntryType, FleetMaintenanceCategory } from './contractsMirror.js';

const ENTRY_TYPES: readonly FleetEntryType[] = ['fuel', 'maintenance'];

const MAINTENANCE_CATEGORIES: readonly FleetMaintenanceCategory[] = [
  'oil_change',
  'tire_rotation',
  'inspection',
  'brakes',
  'tires',
  'engine',
  'transmission',
  'cooling',
  'electrical',
  'other',
];

export interface FleetSearch {
  entryType?: FleetEntryType;
  maintenanceCategory?: FleetMaintenanceCategory;
  archived?: boolean;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** Coerce a raw URL value into a boolean (`1`/`true` → true). */
function asBool(value: unknown): boolean {
  return value === true || value === '1' || value === 'true';
}

/** A real YYYY-MM-DD date, or undefined. */
function asIsoDate(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const d = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? undefined : value;
}

/** Coerce a raw URL value into a trimmed non-empty string, or undefined. */
function asText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

/** Coerce a raw URL value into a known entry type, or undefined. */
function asEntryType(value: unknown): FleetEntryType | undefined {
  return ENTRY_TYPES.find((t) => t === value);
}

/** Coerce a raw URL value into a known maintenance category, or undefined. */
function asMaintenanceCategory(value: unknown): FleetMaintenanceCategory | undefined {
  return MAINTENANCE_CATEGORIES.find((c) => c === value);
}

/**
 * TanStack Router `validateSearch` for the fleet list route. Only keeps each filter when
 * meaningful so the canonical URL stays clean (`/fleet` vs `/fleet?entryType=fuel`).
 */
export function validateFleetSearch(raw: Record<string, unknown>): FleetSearch {
  const search: FleetSearch = {};
  const entryType = asEntryType(raw.entryType);
  if (entryType) search.entryType = entryType;
  const maintenanceCategory = asMaintenanceCategory(raw.maintenanceCategory);
  if (maintenanceCategory) search.maintenanceCategory = maintenanceCategory;
  if (asBool(raw.archived)) search.archived = true;
  const text = asText(raw.search);
  if (text) search.search = text;
  const dateFrom = asIsoDate(raw.dateFrom);
  if (dateFrom) search.dateFrom = dateFrom;
  const dateTo = asIsoDate(raw.dateTo);
  if (dateTo) search.dateTo = dateTo;
  return search;
}
